import React, { useMemo } from 'react';
import { Marker } from 'react-map-gl';
import { animated, config, useTransition } from 'react-spring';
import styled from 'styled-components';
import { MapType } from 'types/types';
import { convertHexToRGBA } from 'utils/color';
import { useActiveContext } from '../../Context/ActiveContext';
import MarkerIcon from './Marker';
import { getPrimaryImage } from 'utils/supplyChain';
import { SiteType } from 'types/site.types';
const ActiveMarker = styled(animated.div) `
  position: relative;
  cursor: pointer;
`;
const InactiveMarker = styled(animated.div) ``;
const InactiveMarkerContainer = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme }) => convertHexToRGBA(theme.palette.primary.main, 0.2)};
`;
const InactiveMarkerInner = styled.div `
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.primary.main};
`;
const MarkersTransition = ({ activities, forStepIndex, linksSerialized, delay, mapType, onMarkerClick, }) => {
    const { activeStepActivity, activeStepActivityId, activeStepIndex, animatedPin, } = useActiveContext();
    const isCurrentIndex = activeStepIndex === forStepIndex;
    // Checks if there's a active node,
    // If so, put the active node last in the nodes array, this makes sure the node's svg is rendered last.
    // svg layering is based on the order ( and not the zIndex ) in which they render, so the last item is always on top.
    // This makes sure the node selected by the user is rendered and visible on the map.
    const sortedActivities = useMemo(() => {
        return activeStepActivity
            ? [
                ...activities.filter((node) => (node === null || node === void 0 ? void 0 : node.id) !== activeStepActivityId),
                activeStepActivity,
            ]
            : activities;
    }, [activities, activeStepActivity, activeStepActivityId]);
    const linksToShow = mapType === MapType.DEFAULT
        ? linksSerialized
        : linksSerialized.filter((node) => {
            return node.chainStepActivityTargetId === activeStepActivityId;
        });
    // @ts-ignore
    const transition = useTransition(sortedActivities, {
        from: { transform: 'scale(0)' },
        enter: { transform: 'scale(1)', delay },
        leave: { transform: 'scale(0)', delay },
        config: config.gentle,
        keys: (item, i) => `marker-transition-${i}`,
    });
    // @ts-ignore
    const fragment = transition((style, { id, activity: { locationCoordinates, mediaList, site } }) => {
        // Checks if the marker is present in any of the links ( lines ) that are showed on the map.
        // If this is the case, we should render the marker on the screen.
        const isPresentInSourceOrDestination = linksToShow.some((step) => {
            const isLinkedToSource = step.chainStepActivitySourceId === id;
            const isLinkedToTarget = step.chainStepActivityTargetId === id;
            return isLinkedToSource || isLinkedToTarget;
        });
        if (!isPresentInSourceOrDestination && !isCurrentIndex) {
            return null;
        }
        // For now we decided to support both locationCoordinates and site. If the site is present, we use the site coordinates.
        // This the locationCoordinates will be removed soon and the site will be the only source of the location.
        const coordinates = (site === null || site === void 0 ? void 0 : site.siteType) === SiteType.CLUSTER
            ? site.centroidCoordinates
            : (site === null || site === void 0 ? void 0 : site.locationCoordinates) || locationCoordinates;
        return (React.createElement(Marker, { key: style.key, latitude: coordinates.lat, longitude: coordinates.lng, 
            // Workaround to fix the markers anchor position on the map (size of the marker image).
            // This will be fixed in the next version of react-map-gl.
            offsetLeft: isCurrentIndex ? -20 : -8, offsetTop: isCurrentIndex ? -50 : -8 }, isCurrentIndex ? (
        // @ts-ignore
        React.createElement(ActiveMarker, { onClick: () => onMarkerClick(id), style: style },
            React.createElement(MarkerIcon, { active: id === activeStepActivityId, image: getPrimaryImage(mediaList), renderedOn: "MAP", activeNodeId: activeStepActivityId, animatedPin: animatedPin }))) : (React.createElement(InactiveMarker, { style: style },
            React.createElement(InactiveMarkerContainer, null,
                React.createElement(InactiveMarkerInner, null))))));
    });
    return fragment;
};
export default MarkersTransition;
